import { useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { formatChf, toRappen } from '@raporto/shared'
import { ActionButton } from '../ui/ActionButton.js'
import { notify, notifyError } from '../ui/Toasts.js'
import { useCacheTable, useForm, useSearch } from '../ui/hooks.js'
import { CATALOG_SEARCH } from '../net/search.js'
import {
  createCatalogItem,
  removeCatalogItem,
  updateCatalogItem,
  type CatalogFields
} from '../net/records.js'
import type { CacheRow } from '../net/localDb.js'

export function Catalog({ session }: { session: SessionUser }): JSX.Element {
  const { rows, loading } = useCacheTable<CacheRow>('catalogItems')
  const { query, setQuery, results, tookMs } = useSearch(rows, CATALOG_SEARCH)
  const [editing, setEditing] = useState<CacheRow | null>(null)
  const [creating, setCreating] = useState(false)
  const [showInactive, setShowInactive] = useState(false)
  const canEdit = session.role === 'superuser'
  const seesPrices = session.role === 'superuser' || session.workerSeesPrices

  const visible = showInactive ? results : results.filter((r) => r.active !== false)

  return (
    <>
      <div className="card">
        <div className="listhead">
          <h2>Leistungen</h2>
          {/*
            Die Zahl zählt, was darunter steht – nicht, was in der Datenbank
            liegt. Vorher stand hier rows.length: nach dem Deaktivieren von
            neun Dubletten meldete die Überschrift 27 und die Liste zeigte 18.
            Wer das sieht, sucht neun Positionen, die es nicht gibt.
          */}
          <span className="muted">
            {visible.length}
            {!showInactive && rows.length > visible.length ? ` von ${rows.length}` : ''}
          </span>
        </div>
        {!seesPrices && (
          <p className="muted small">
            Preise sind für deine Rolle nicht freigegeben – sie liegen deshalb auch nicht auf diesem
            Gerät.
          </p>
        )}
        <input
          autoComplete="off"
          id="katalog-suche"
          type="search"
          placeholder="Code oder Bezeichnung suchen …"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query && (
          <p className="muted small">
            {visible.length} Treffer · lokal gesucht in {tookMs} ms
          </p>
        )}
        <label className="inline">
          <input
            autoComplete="off"
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Inaktive und deaktivierte anzeigen
        </label>

        {loading ? (
          <p className="muted">Wird geladen …</p>
        ) : visible.length === 0 ? (
          <p className="muted">
            {query ? `Keine Leistung mit „${query}“.` : 'Noch keine Leistungen erfasst.'}
          </p>
        ) : (
          <ul className="records">
            {visible.map((i) => (
              <li key={i.id}>
                <button type="button" onClick={() => canEdit && setEditing(i)}>
                  <span className="rec-name">
                    {i.code ? <span className="code">{String(i.code)}</span> : null}
                    {String(i.name)}
                    {i.pending === true && <span className="pill small">wartet</span>}
                    {i.active === false && (
                      <span className="pill small">{i.archivedAt ? 'deaktiviert' : 'inaktiv'}</span>
                    )}
                  </span>
                  <span className="rec-sub">
                    {String(i.kind) === 'material' ? 'Material' : 'Arbeit'} · {String(i.unit)}
                    {seesPrices && i.unitPriceRp !== null && i.unitPriceRp !== undefined
                      ? ` · CHF ${formatChf(Number(i.unitPriceRp))}`
                      : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {canEdit && (
          <ActionButton
            variant="secondary"
            labels={{ idle: 'Neue Leistung', busy: 'Öffnet …' }}
            onAction={async () => setCreating(true)}
          />
        )}
      </div>

      {canEdit && (creating || editing) && (
        <CatalogForm
          session={session}
          record={editing}
          onDone={() => {
            setCreating(false)
            setEditing(null)
          }}
        />
      )}
    </>
  )
}

function CatalogForm({
  session,
  record,
  onDone
}: {
  session: SessionUser
  record: CacheRow | null
  onDone: () => void
}): JSX.Element {
  const { values, set } = useForm<CatalogFields & { priceText: string }>(
    record
      ? {
          code: String(record.code ?? ''),
          name: String(record.name ?? ''),
          unit: String(record.unit ?? 'Std.'),
          unitPriceRp: Number(record.unitPriceRp ?? 0),
          priceText: formatChf(Number(record.unitPriceRp ?? 0)),
          vatRate: Number(record.vatRate ?? 8.1),
          kind: (record.kind as 'arbeit' | 'material') ?? 'arbeit',
          active: record.active !== false
        }
      : {
          code: '',
          name: '',
          unit: 'Std.',
          unitPriceRp: 0,
          priceText: '',
          vatRate: 8.1,
          kind: 'arbeit',
          active: true
        }
  )
  const actor = { tenantId: session.tenantId, userId: session.userId }

  return (
    <div className="card">
      <h2>{record ? 'Leistung bearbeiten' : 'Neue Leistung'}</h2>

      <div className="row">
        <div className="narrow">
          <label htmlFor="c-code">Code</label>
          <input
            autoComplete="off"
            id="c-code"
            value={values.code}
            onChange={(e) => set('code', e.target.value)}
          />
        </div>
        <div className="grow">
          <label htmlFor="c-name">Bezeichnung</label>
          <input
            autoComplete="off"
            id="c-name"
            value={values.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>
      </div>

      <div className="row">
        <div className="narrow">
          <label htmlFor="c-unit">Einheit</label>
          <input
            autoComplete="off"
            id="c-unit"
            value={values.unit}
            onChange={(e) => set('unit', e.target.value)}
          />
        </div>
        <div className="grow">
          <label htmlFor="c-price">Preis in CHF</label>
          <input
            autoComplete="off"
            id="c-price"
            inputMode="decimal"
            value={values.priceText}
            placeholder="0.00"
            onChange={(e) => set('priceText', e.target.value)}
          />
        </div>
      </div>
      <p className="muted small">
        Gespeichert wird in Rappen – aus 45.00 werden 4500. Deshalb gibt es hier nie Rundungsfehler.
      </p>

      <div className="row">
        <div className="grow">
          <label htmlFor="c-kind">Art</label>
          <select
            id="c-kind"
            value={values.kind}
            onChange={(e) => set('kind', e.target.value as 'arbeit' | 'material')}
          >
            <option value="arbeit">Arbeit</option>
            <option value="material">Material</option>
          </select>
        </div>
      </div>

      <label className="inline">
        <input
          autoComplete="off"
          type="checkbox"
          checked={values.active !== false}
          onChange={(e) => set('active', e.target.checked)}
        />
        Aktiv
      </label>

      <div className="actions-row">
        <ActionButton
          id="btn-katalog-speichern"
          labels={{ idle: 'Speichern', busy: 'Speichert …', success: 'Gespeichert' }}
          onAction={async () => {
            if (!values.name.trim()) {
              notify.warn('Ohne Bezeichnung geht es nicht.')
              return
            }
            let priceRp = 0
            try {
              priceRp = values.priceText.trim() === '' ? 0 : toRappen(values.priceText)
            } catch {
              notify.warn('Der Preis ist kein gültiger Betrag.')
              return
            }
            const fields: CatalogFields = {
              code: values.code,
              name: values.name,
              unit: values.unit,
              unitPriceRp: priceRp,
              vatRate: values.vatRate,
              kind: values.kind,
              active: values.active
            }
            if (record) {
              await updateCatalogItem(actor, String(record.id), fields, Number(record.version ?? 1))
            } else {
              await createCatalogItem(actor, fields)
            }
            onDone()
          }}
          onError={(err) => notifyError(err)}
        />
        <ActionButton
          variant="quiet"
          labels={{ idle: 'Abbrechen', busy: '…' }}
          onAction={async () => onDone()}
        />
        {record && (
          <ActionButton
            variant="quiet"
            actionClass="B"
            className="danger"
            labels={{
              idle: 'Löschen',
              busy: 'Prüft Verwendung …',
              explain: 'Suche Rapporte und Belege …',
              success: 'Erledigt'
            }}
            onAction={async () => {
              const r = await removeCatalogItem(actor, String(record.id))
              if (r.disposal === 'geloescht') notify.ok(r.message)
              else notify.warn(r.message)
              onDone()
            }}
            onError={(err) => notifyError(err)}
          />
        )}
      </div>
      {record && (
        <p className="muted small">
          Steht die Leistung schon auf einem Rapport oder einem Beleg, wird sie deaktiviert statt
          gelöscht. Bestehende Zeilen tragen ihren eigenen Text und Preis und ändern sich dadurch
          nicht.
        </p>
      )}
    </div>
  )
}
